// pages/ContactList.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
    deleteContactAPI,
    getAllContactsAPI,
} from "../../../../api/contactAPIs.ts"; // Adjust path as needed
import {
    BlockBetween,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    PreviewCard,
    ReactDataTable,
} from "../../../../components/Component";
import DeleteModal from "../../../../components/DeleteModal";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const ContactList = () => {
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);

    const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);

    const handleDelete = async (contact) => {
        try {
            await deleteContactAPI({ id: contact._id });
            setContacts((prev) => prev.filter((c) => c._id !== contact._id));
            toast.success("Contact request deleted successfully");
        } catch (error) {
            toast.error("Failed to delete contact request");
            console.error("Delete error:", error);
        }
    };

    useEffect(() => {
        const fetchContacts = async () => {
            try {
                setLoading(true);
                const response = await getAllContactsAPI();
                const fetchedContacts = response.data.data; // Assuming response structure matches your API
                setContacts(fetchedContacts);
            } catch (error) {
                console.error("Failed to fetch contacts:", error);
                toast.error("Failed to fetch contacts");
            } finally {
                setLoading(false);
            }
        };

        fetchContacts();
    }, []);

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            cell: (row) => <span className="fw-bold">{row.name}</span>,
            sortable: true,
        },
        {
            name: "Phone",
            selector: (row) => row.phone,
            cell: (row) => <span className="text-muted">{row.phone}</span>,
            sortable: true,
        },
        {
            name: "Category",
            selector: (row) => row.category,
            cell: (row) => <span>{row.category}</span>,
            sortable: true,
        },
        {
            name: "Message",
            selector: (row) => row.message || "",
            cell: (row) => (
                <span className="text-muted">
                    {row.message?.substring(0, 50) || "No message"}
                    {row.message?.length > 50 ? "..." : ""}
                </span>
            ),
        },
        {
            name: "Date",
            selector: (row) => row.createdAt,
            cell: (row) => (
                <span>{new Date(row.createdAt).toLocaleDateString()}</span>
            ),
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                            setSelectedContact(row);
                            toggleDeleteModal();
                        }}
                    >
                        <i className="bi bi-trash-fill"></i> Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Head title="Contact List" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Contact List
                            </BlockTitle>
                            <BlockDes className="text-soft">
                                <p>You have a total of {contacts.length} contact requests.</p>
                            </BlockDes>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <BlockContent>
                    <PreviewCard>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                <div className="text-center">
                                    <div
                                        className="spinner-grow text-primary"
                                        style={{ width: "3rem", height: "3rem" }}
                                        role="status"
                                    >
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading contact requests, please wait...</p>
                                </div>
                            </div>
                        ) : (
                            <ReactDataTable
                                data={contacts}
                                columns={columns}
                                pagination
                                actions // Enables export buttons
                                expandableRows // Makes it responsive for mobile
                            />
                        )}
                    </PreviewCard>
                </BlockContent>
            </Content>
            <DeleteModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDeleteModal}
                onConfirm={handleDelete}
                item={selectedContact}
                itemName="contact request"
            />
        </React.Fragment>
    );
};

export default ContactList;